import React, { useEffect, useState } from 'react';
import ImageGrid from './ImageGrid';
import AppliedFilters from './AppliedFilters';
import ContentPageHeader from './PageHeader';
import { ImageMetadata, fetchPaginatedImages } from '../services/api';
import { formatDateYmd } from '../utils/time';
import { getConfig } from '../config';
import Pagination from './Pagination';
import { useContent } from '../contexts/ContentContext';
import { imageHasColorToken } from '../utils/colorPalette';
import { useRestoreListPosition } from './BackToListButton';
interface WordGalleryProps {
    word: string;
    mode?: 'word' | 'color';
    onDatesChange?: (dates: string[]) => void;
    onImagesChange?: (images: ImageMetadata[]) => void;
    colorFilter?: string | null;
}

const WordGallery: React.FC<WordGalleryProps> = ({ word, mode = 'word', onDatesChange, onImagesChange, colorFilter = null }) => {
    const { content, language, t } = useContent();
    const [images, setImages] = useState<ImageMetadata[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [gridWidth, setGridWidth] = useState<number | null>(null);

    // Pagination state
    const urlParams = new URLSearchParams(window.location.search);
    const initialPageNumber = parseInt(urlParams.get('pageNumber') || '1', 10);
    const [currentPage, setCurrentPage] = useState(initialPageNumber);
    const [totalItems, setTotalItems] = useState(0);
    const { thumbnailsPerPage } = getConfig();
    const { apiHost } = getConfig();
    useEffect(() => {
        setCurrentPage(initialPageNumber);
    }, [initialPageNumber, word, mode]);

    const handlePageChange = (nextPage: number) => {
        setCurrentPage(nextPage);
        const params = new URLSearchParams(window.location.search);
        params.set('page', mode);
        if (mode === 'color') {
            params.delete('word');
            params.delete('tags');
            params.set('color', word);
        } else if (params.get('tags')) {
            params.delete('word');
        } else {
            params.set('word', word);
        }
        params.delete('mode');
        params.delete('hash');
        params.delete('view');
        params.delete('year');
        params.delete('month');
        params.delete('id');
        if (nextPage <= 1) {
            params.delete('pageNumber');
        } else {
            params.set('pageNumber', String(nextPage));
        }
        window.history.pushState({}, '', `/?${params.toString()}${window.location.hash}`);
    };

    useEffect(() => {
        const fetchWordData = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await fetchPaginatedImages(mode, {
                    w: mode === 'word' ? word : undefined,
                    c: mode === 'color' ? word : undefined,
                    page: currentPage,
                    per_page: thumbnailsPerPage
                });

                if (result && result.items) {
                    const imageMetadataList: ImageMetadata[] = result.items.map((item: any) => {
                        let thumbUrl = item.thumb;
                        // If already a full URL, use as-is
                        if (thumbUrl && thumbUrl.startsWith('http')) {
                            // Already a full URL, no modification needed
                        } else if (thumbUrl) {
                            // Normalize path - ensure it starts with /room_image/
                            if (thumbUrl.startsWith('/room_image/')) {
                                // Path already includes /room_image/, just prepend host
                                thumbUrl = `${apiHost.replace(/\/+$/, '')}${thumbUrl}`;
                            } else if (thumbUrl.startsWith('/')) {
                                // Path starts with / but not /room_image/
                                thumbUrl = `${apiHost.replace(/\/+$/, '')}${thumbUrl}`;
                            } else {
                                // Relative path, prepend full base
                                thumbUrl = `${apiHost.replace(/\/+$/, '')}/room_image/${thumbUrl}`;
                            }
                        }

                        return {
                            ...item,
                            thumb: thumbUrl,
                            tags: item.tags || [],
                        };
                    });

                    setImages(imageMetadataList);
                    onImagesChange?.(imageMetadataList);
                    setTotalItems(result.pagination.total_items);
                } else {
                    setImages([]);
                    onImagesChange?.([]);
                    setTotalItems(0);
                }
            } catch (err) {
                console.error(err);
                setError(content.ui[language].wordGallery.error);
                onImagesChange?.([]);
            } finally {
                setLoading(false);
            }
        };

        fetchWordData();
    }, [word, mode, currentPage, thumbnailsPerPage, apiHost, content.ui, language, onImagesChange]);

    // Pagination logic
    const paginatedImages = colorFilter
        ? images.filter(image => imageHasColorToken(image, colorFilter))
        : images; // Images are already paginated
    const totalPages = Math.max(1, Math.ceil(totalItems / thumbnailsPerPage));
    const normalizedCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);
    const firstVisibleImage = totalItems > 0 ? (normalizedCurrentPage - 1) * thumbnailsPerPage + 1 : 0;
    const lastVisibleImage = totalItems > 0 ? Math.min(normalizedCurrentPage * thumbnailsPerPage, totalItems) : 0;
    const rangeStatusLabel = t('common.rangeStatus', { first: firstVisibleImage, last: lastVisibleImage, total: totalItems });

    // Extract dates for sidebar
    useEffect(() => {
        if (onDatesChange && paginatedImages.length > 0) {
            const dates = Array.from(new Set(
                paginatedImages
                    .map(image => formatDateYmd(image.time))
                    .filter((date): date is string => Boolean(date))
            )).sort((a, b) => b.localeCompare(a));
            onDatesChange(dates);
        }
    }, [paginatedImages, onDatesChange]);

    const heading = <ContentPageHeader
        title={t('common.wordTitle', { label: mode === 'color' ? t('searchWordGallery.text1') : t('header.word'), word })}
        count={!loading && !error ? rangeStatusLabel : undefined}>
        <AppliedFilters />
        {!loading && !error && totalItems > 0 && <Pagination currentPage={currentPage} totalItems={totalItems} itemsPerPage={thumbnailsPerPage} onPageChange={handlePageChange} className="lab-list-pagination" />}
    </ContentPageHeader>;

    useRestoreListPosition(!loading && !error);
    if (loading) return <section>{heading}<p role="status">{content.ui[language].wordGallery.loading.replace('{word}', word)}</p></section>;
    if (error) return <section>{heading}<p role="alert">{error}</p><button className="lab-button" onClick={() => window.location.reload()}>{t('common.retry')}</button></section>;

    return (
        <div className="lab-content-page">
            {heading}
            {images.length > 0 ? (
                <>
                    {paginatedImages.length > 0 ? (
                        <ImageGrid images={paginatedImages} year={null} month={null} word={mode === 'word' ? word : undefined} onGridWidthChange={setGridWidth} />
                    ) : (
                        <p className="dark:text-gray-400">{content.ui[language].wordGallery.empty}</p>
                    )}
                    <Pagination
                        currentPage={currentPage}
                        totalItems={totalItems}
                        itemsPerPage={thumbnailsPerPage}
                        onPageChange={handlePageChange}
                        gridWidth={gridWidth}
                    />
                </>
            ) : (
                <p className="dark:text-gray-400">{content.ui[language].wordGallery.empty}</p>
            )}
        </div>
    );
};

export default WordGallery;
